// L'en-tête du devis : client, projet, délai, validité, TVA.
//
// Extrait de `DevisPage.jsx` pour tenir la règle des 250 lignes dans
// `features/` — chez AGC, un seul fichier faisait 1 657 lignes et
// mélangeait quatre domaines. Ce composant ne décide rien : il affiche
// et il remonte les modifications.

import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import { TextField, Textarea } from '../../ui/TextField.jsx';
import ClientPicker from '../../ui/ClientPicker.jsx';
import NumberField from '../../ui/NumberField.jsx';
import Button from '../../ui/Button.jsx';
import { StatusBadge } from '../../ui/Badge.jsx';
import SupprimerDevis from './SupprimerDevis.jsx';
import NouvelleDemande from './NouvelleDemande.jsx';
import { useSession } from '../../lib/session.jsx';
import { LIBELLES_STATUT } from '@shared/schemas.js';

/**
 * Où en est l'enregistrement automatique — discret, et jamais muet sur
 * un échec : un devis qui ne part pas doit le dire (6 octobre 2026).
 */
function EtatEnregistrement({ statut, dirty, onReessayer }) {
  if (statut?.etat === 'envoi') return <span className="dirty-flag">Enregistrement…</span>;
  if (statut?.etat === 'echec') {
    return (
      <Button size="sm" variant="danger" onClick={onReessayer} title={statut.erreur?.message}>
        Échec — réessayer
      </Button>
    );
  }
  if (dirty) return <span className="dirty-flag">Non enregistré</span>;
  if (statut?.a) {
    const hh = statut.a.toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });
    return <span className="field__hint">Enregistré à {hh}</span>;
  }
  return null;
}

export function DevisHeaderCard({
  editor, locked, saving, enregistrement, onSave, onCreateRevision, onSupprime,
}) {
  const { devis } = editor;
  // Un brouillon se supprime par tout utilisateur, le reste par l'admin —
  // la même règle que le serveur (6 octobre 2026). On n'offre pas à
  // l'écran ce que le serveur refusera : un bouton qui rend 403 apprend à
  // l'utilisateur que l'application est cassée, pas qu'il n'a pas le droit.
  const { isAdmin } = useSession();
  return (
    <Card>
      <CardHead title={`Devis ${devis.reference}`}>
        <StatusBadge status={devis.status} />
        {locked ? null : (
          <EtatEnregistrement statut={enregistrement} dirty={editor.dirty} onReessayer={onSave} />
        )}
        {locked ? (
          <>
            <NouvelleDemande devis={devis} nbPieces={editor.pieces?.length ?? 0} />
            <Button size="sm" variant="secondary" onClick={onCreateRevision}>
              Créer une révision
            </Button>
          </>
        ) : (
          <Button size="sm" variant="primary" onClick={onSave} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        )}
        {isAdmin || devis.status === 'draft' ? (
          <SupprimerDevis
            devis={devis}
            pieces={editor.pieces?.length ?? 0}
            onSupprime={onSupprime}
          />
        ) : null}
      </CardHead>
      <CardBody className="stack">
        {locked ? (
          <p className="field__hint">
            Ce devis est {(LIBELLES_STATUT[devis.status] ?? devis.status).toLowerCase()} : il n’est plus
            modifiable. Le client redemande ces pièces (une autre quantité, une
            autre commande) : « Nouvelle demande » ouvre un nouveau devis, avec son propre
            numéro. L’offre envoyée est à corriger : « Créer une révision » reprend la référence
            suffixée, et l’original reste consultable tel qu’il est parti.
          </p>
        ) : null}

        {/* ── SIX CHAMPS COURTS, PAS SIX LIGNES ──
            Cette carte occupait 355 px de haut pour un client, un
            projet, une référence, un délai, une validité et un taux de
            TVA — six valeurs qui tiennent en deux lignes. Sur un écran
            qui en fait 7 900, chaque bloc doit payer sa place, et
            celui-ci ne la payait pas.

            `grid-4` serre à quatre colonnes dès qu'il y a la largeur, et
            retombe tout seul en dessous. Les deux champs les plus
            étroits — validité et TVA — n'ont aucune raison d'occuper une
            demi-largeur chacun. */}
        {/* ── LE MANQUE EST UN SIGNAL, PAS UNE ABSENCE — 28 août 2026 ──
            Un devis né d'un mail dont l'entreprise n'a pas été reconnue
            existe et se chiffre. Ce qu'il ne peut pas, c'est PARTIR :
            l'envoi le dit à son tour, au moment où ça compte. */}
        {!devis.client_id && (
          <p className="devis-sans-client-bandeau">
            <strong>Client à rattacher.</strong> Ce devis a été créé sans que
            l’entreprise soit reconnue — choisissez-la ci-dessous. Le devis se
            chiffre dès maintenant&nbsp;; il ne pourra pas être envoyé avant.
          </p>
        )}
        <div className="grid-4">
          {/* Le client courant vient du DEVIS, pas d'une liste chargée.
              La liste était bornée à 100 : au-delà de la centième
              société par ordre alphabétique, le menu n'affichait aucun
              nom sur un devis parfaitement valide. */}
          <ClientPicker
            label="Client"
            disabled={locked}
            /* `null` et non un objet aux champs vides : le composant
               distingue les deux, et un objet vide lui ferait afficher
               « Changer de client… » sur un devis qui n'en a aucun. */
            selected={devis.client_id ? { id: devis.client_id, company: devis.client_company } : null}
            /* Le NOM avec l'identifiant : le champ affiche `client_company`,
               et sans lui un devis né sans client restait vide après le
               choix (CALYOS, 29 septembre 2026). */
            onSelect={(c) => editor.patchDevis({ client_id: c.id, client_company: c.company })}
          />
          <TextField label="Projet" value={devis.project ?? ''} disabled={locked}
            onChange={(e) => editor.patchDevis({ project: e.target.value })} />
          <TextField label="Référence commande client" value={devis.client_order_ref ?? ''}
            disabled={locked}
            onChange={(e) => editor.patchDevis({ client_order_ref: e.target.value })} />
          <TextField label="Délai" value={devis.delay ?? ''} disabled={locked}
            onChange={(e) => editor.patchDevis({ delay: e.target.value })} />
          <NumberField label="Validité" unit="jours" value={devis.validity_days}
            min={0} disabled={locked}
            onChange={(v) => editor.patchDevis({ validity_days: v })} />
          <NumberField label="TVA" unit="%" value={devis.vat_rate} min={0} max={100}
            disabled={locked}
            onChange={(v) => editor.patchDevis({ vat_rate: v })} />
        </div>

        {/* Les notes client se remplissent une fois sur dix devis : elles
            n'ont pas à coûter 80 px sur les neuf autres. Repliées, mais
            OUVERTES d'office quand elles portent quelque chose — un
            texte caché qui part sur l'offre serait pire que le place
            qu'il prend. */}
        <details className="repli repli--champs" open={Boolean(devis.notes_client)}>
          <summary>Notes pour le client{devis.notes_client ? ' — remplies' : ''}</summary>
          <Textarea label="Notes pour le client" hideLabel value={devis.notes_client ?? ''}
            rows={2} disabled={locked}
            onChange={(e) => editor.patchDevis({ notes_client: e.target.value })} />
        </details>
      </CardBody>
    </Card>
  );
}

export default DevisHeaderCard;
